import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, MapPin, Calendar, Clock, CreditCard, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingConfirmation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error("Error fetching booking:", error);
      toast.error("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600 mb-4">Booking not found</p>
            <Button onClick={() => navigate("/")} data-testid="home-btn-error">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="/elite-cwb-logo.webp" 
                alt="Elite CWB Logo" 
                className="h-24 w-auto" style={{ filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.3))' }}
              />
            </div>
            <div className="flex items-center gap-4">
              {user && (
                <Button 
                  data-testid="dashboard-confirmation-nav-btn"
                  variant="ghost" 
                  onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                >
                  Dashboard
                </Button>
              )}
              <Button 
                data-testid="home-confirmation-btn"
                variant="outline" 
                onClick={() => navigate("/")}
              >
                Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Success Message */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Booking Confirmed!</h1>
            <p className="text-lg text-gray-600">
              Your booking has been successfully created. Please complete the deposit payment to secure your reservation.
            </p>
          </div>

          {/* Booking Details Card */}
          <Card className="shadow-xl border-2 mb-6">
            <CardHeader className="border-b">
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">Booking Details</CardTitle>
                <Badge className="bg-yellow-500">PENDING PAYMENT</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Service Information</h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-gray-600 text-sm">Vehicle:</span>
                        <p className="font-semibold">{booking.vehicle_type}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Service Type:</span>
                        <p className="font-semibold">
                          {booking.booking_type === "airport_transfer" 
                            ? "Airport Transfer" 
                            : booking.booking_type === "as_directed_inner" 
                            ? "As Directed (Inner London)" 
                            : "As Directed (Outside London)"}
                        </p>
                      </div>
                      {booking.route && (
                        <div>
                          <span className="text-gray-600 text-sm">Route:</span>
                          <p className="font-semibold">{booking.route}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Journey Details</h3>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-600 mt-1" />
                        <div>
                          <span className="text-gray-600 text-sm">Pickup:</span>
                          <p className="font-semibold">{booking.pickup_location}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-600 mt-1" />
                        <div>
                          <span className="text-gray-600 text-sm">Drop-off:</span>
                          <p className="font-semibold">{booking.dropoff_location}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-600" />
                        <span className="font-semibold">{booking.date} at {booking.time}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Section */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Payment Information</h3>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Total Price</p>
                      <p className="text-2xl font-bold text-gray-900">£{booking.total_price}</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
                      <p className="text-sm text-gray-600 mb-1">Deposit Due (20%)</p>
                      <p className="text-2xl font-bold text-amber-700">£{booking.deposit_amount}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Pay on Day</p>
                      <p className="text-2xl font-bold text-gray-900">£{booking.total_price - booking.deposit_amount}</p>
                    </div>
                  </div>
                  
                  {!booking.deposit_paid ? (
                    <div className="p-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border-2 border-amber-200">
                      <div className="flex items-start gap-4">
                        <CreditCard className="w-8 h-8 text-amber-600 mt-1" />
                        <div className="flex-1">
                          <h4 className="font-bold text-lg text-gray-900 mb-2">Complete Your Payment</h4>
                          <p className="text-gray-700 mb-4">
                            To secure your booking, please pay the deposit of <strong>£{booking.deposit_amount}</strong>. 
                            The remaining amount of <strong>£{booking.total_price - booking.deposit_amount}</strong> can be paid on the day of service via cash or card.
                          </p>
                          <Button 
                            data-testid="pay-deposit-confirmation-btn"
                            onClick={() => window.open(booking.payment_link, '_blank')}
                            className="bg-gradient-to-r from-amber-600 to-amber-500 text-white hover:from-amber-700 hover:to-amber-600"
                            size="lg"
                          >
                            Pay Deposit Now (£{booking.deposit_amount})
                          </Button>
                          <p className="text-sm text-gray-600 mt-3">
                            After completing payment, our team will confirm your booking within 24 hours.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                      <div className="flex items-start gap-4">
                        <CheckCircle className="w-8 h-8 text-green-600 mt-1" />
                        <div className="flex-1">
                          <h4 className="font-bold text-lg text-gray-900 mb-2">Payment Received!</h4>
                          <p className="text-gray-700 mb-2">
                            Thank you! Your deposit of <strong>£{booking.deposit_amount}</strong> has been received and your booking is confirmed.
                          </p>
                          <p className="text-gray-700">
                            The remaining balance of <strong>£{booking.total_price - booking.deposit_amount}</strong> can be paid on the day of service via cash or card.
                          </p>
                          <div className="mt-4 p-3 bg-white/50 rounded-lg">
                            <p className="text-sm text-gray-700">
                              <strong>What's next?</strong> Our chauffeur will contact you 24 hours before your journey to confirm all details.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Booking Reference */}
                <div className="border-t pt-6">
                  <p className="text-sm text-gray-600 mb-1">Booking Reference</p>
                  <p className="font-mono text-lg font-semibold">{booking.id.toUpperCase()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              data-testid="back-home-confirmation-btn"
              onClick={() => navigate("/")} 
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            {user && (
              <Button 
                data-testid="view-bookings-btn"
                onClick={() => navigate("/dashboard")} 
                className="flex-1 bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600"
              >
                View My Bookings
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;